import "./index.scss";

import { type ReactNode, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import Button from "common/core/button";
import { DeprecatedFormRow } from "common/form/elements/row";
import { DeprecatedStyledTextInput } from "common/form/inputs/text";
import TosV2 from "common/tos";
import { PAYMENT_CARD, PAYMENT_ACH } from "constants/organization";
import { Label } from "common/core/form/layout";
import { Pill } from "common/core/pill_tabs";
import type { Payer } from "graphql_globals";

import { AchForm } from "../ach_form";
import { CardForm } from "../card_form";
import type { PaymentSource } from "../inline_form";

const MESSAGES = defineMessages({
  cardName: {
    id: "f0f8d2e6-bc4a-42b8-9527-9fa9a07c027e",
    defaultMessage: "Cardholder Name",
  },
  tosActionNew: {
    id: "d65fb63a-291e-4348-a73a-fb1f81489475",
    defaultMessage: "By changing your plan and clicking 'Submit Payment'",
  },
  delayedCharge: {
    id: "f8ae7489-5408-4507-a609-f61412125386",
    defaultMessage: "Charge will not be made until end of payment period",
  },
  submit: {
    id: "3b23efc2-9623-42b8-8a1a-23cea809e8e3",
    defaultMessage: "Save payment",
  },
  changePayment: {
    id: "fed0179d-8797-4fd6-90ed-b9a9b7dd10fa",
    defaultMessage: "Change Payment",
  },
  useExistingPayment: {
    id: "9f7b0414-453e-4742-9682-be9ef8233245",
    defaultMessage: "Use Existing Payment",
  },
  last4: {
    id: "f9042ece-eeb8-4674-8801-2b909061064e",
    defaultMessage: "Last 4",
  },
  accountName: {
    id: "30c9c95c-e800-4e25-8688-df1b1cc1b329",
    defaultMessage: "Account Name",
  },
  creditCard: {
    id: "c016e2a6-ee8f-4c23-ad23-34494ceb19d3",
    defaultMessage: "Credit card",
  },
  bankAccount: {
    id: "74e6105f-a850-4f03-ad06-801a428b1811",
    defaultMessage: "Bank account",
  },
  cancel: {
    id: "62cec447-798f-4052-9568-1a2683f71d94",
    defaultMessage: "Cancel",
  },
  accountConnected: {
    id: "2614d7e9-7ac5-414c-add0-aef3a31f1eb6",
    defaultMessage: "Account connected",
  },
});

type Props = {
  showSubscriptionTos?: boolean;
  showDelayedChargeMessage?: boolean;
  currentPaymentSource: PaymentSource | null;
  submitButtonLabel?: ReactNode;
  stripeElementOptionStyles: {
    placeholder: string;
  };
  onClose?: () => void;
  collectPayment: boolean;
  organizationId: string;
  payer: Payer;
  onChangesSaved: () => void;
};

export function PaymentModalForm({
  showSubscriptionTos,
  showDelayedChargeMessage,
  currentPaymentSource,
  submitButtonLabel,
  onClose,
  collectPayment,
  organizationId,
  payer,
  onChangesSaved,
}: Props) {
  const intl = useIntl();
  const [selectedPaymentSource, setSelectedPaymentSource] = useState(
    currentPaymentSource?.type === "AchAccount" ? PAYMENT_ACH : PAYMENT_CARD,
  );
  const [shouldCollectPayment, setShouldCollectPayment] = useState(collectPayment);

  return (
    <div className="PaymentModalDetails">
      {showDelayedChargeMessage && (
        <div
          data-automation-id="payment-delayed-charge-message"
          className="PaymentModalDetails--delayed-charge-message"
        >
          {intl.formatMessage(MESSAGES.delayedCharge)}
        </div>
      )}

      {shouldCollectPayment && (
        <>
          <Label>
            <FormattedMessage
              id="bfabfecf-75cd-48d8-b7fb-e34214200cd3"
              defaultMessage="Payment method"
            />
          </Label>
          <div className="PaymentModalDetails--pill-group">
            <Pill
              onClick={() => setSelectedPaymentSource(PAYMENT_CARD)}
              aria-label={intl.formatMessage(MESSAGES.creditCard)}
              selected={selectedPaymentSource === PAYMENT_CARD}
              className="PaymentModalDetails--pill"
            >
              {intl.formatMessage(MESSAGES.creditCard)}
            </Pill>
            <Pill
              onClick={() => setSelectedPaymentSource(PAYMENT_ACH)}
              aria-label={intl.formatMessage(MESSAGES.bankAccount)}
              selected={selectedPaymentSource === PAYMENT_ACH}
              automationId="pill-ach"
            >
              {intl.formatMessage(MESSAGES.bankAccount)}
            </Pill>
          </div>
        </>
      )}
      {currentPaymentSource && !shouldCollectPayment && (
        <>
          <DeprecatedFormRow>
            <DeprecatedStyledTextInput
              automationId="current-payment-name"
              value={currentPaymentSource.name}
              label={
                selectedPaymentSource === PAYMENT_CARD
                  ? intl.formatMessage(MESSAGES.cardName)
                  : intl.formatMessage(MESSAGES.accountName)
              }
              disabled
            />
          </DeprecatedFormRow>
          <DeprecatedFormRow>
            <DeprecatedStyledTextInput
              automationId="current-payment-last4"
              value={currentPaymentSource.last4}
              label={intl.formatMessage(MESSAGES.last4)}
              disabled
            />
          </DeprecatedFormRow>
        </>
      )}

      {shouldCollectPayment && selectedPaymentSource === PAYMENT_CARD && (
        <CardForm
          organizationId={organizationId}
          payer={payer}
          onChangesSaved={onChangesSaved}
          submitButtonLabel={submitButtonLabel}
        />
      )}

      {shouldCollectPayment && selectedPaymentSource === PAYMENT_ACH && (
        <AchForm
          organizationId={organizationId}
          paymentSource={currentPaymentSource}
          tooltipDescription={false}
        />
      )}

      {showSubscriptionTos && (
        <div
          data-automation-id="payment-subscription-tos"
          className="PaymentModalDetails--subscription-tos"
        >
          <TosV2 actionText={intl.formatMessage(MESSAGES.tosActionNew)} />
        </div>
      )}

      <div>
        {shouldCollectPayment ? (
          <div className="PaymentModalDetails--buttons">
            {onClose && (
              <Button buttonColor="dark" variant="tertiary" key="cancel" onClick={onClose}>
                <FormattedMessage
                  id="5c4d3f96-c6c8-47cf-8ff6-7fa2fd99d69d"
                  defaultMessage="Cancel"
                />
              </Button>
            )}
          </div>
        ) : (
          <>
            <Button
              automationId="payment-use-existing"
              className="PaymentModalDetails--button"
              onClick={onChangesSaved}
              buttonColor="action"
              buttonSize="large"
              variant="primary"
              fullwidth
            >
              {intl.formatMessage(MESSAGES.useExistingPayment)}
            </Button>
            <Button
              automationId="payment-change"
              className="PaymentModalDetails--button"
              onClick={() => setShouldCollectPayment(true)}
              buttonColor="action"
              buttonSize="large"
              variant="secondary"
              fullwidth
            >
              {intl.formatMessage(MESSAGES.changePayment)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
